import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO42000_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO 42000 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Sustainable Procurement — International standard for integrating
            environmental, social, and ethical considerations into procurement
            processes.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO 42000 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO 42000 is the international standard for Sustainable
              Procurement, providing a framework for organizations to
              incorporate sustainability into their procurement processes. This
              standard guides businesses in making procurement decisions that
              prioritize environmental sustainability, social responsibility,
              and ethical considerations in the supply chain. ISO 42000 aims to
              integrate sustainability into purchasing decisions, ensuring that
              products and services are sourced responsibly, aligning with
              global sustainability goals and corporate social responsibility
              (CSR) objectives.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO 42000 certification demonstrates an organization's
              commitment to sustainable procurement practices and ethical
              sourcing. It ensures that procurement activities contribute
              positively to economic, social, and environmental outcomes,
              minimizing adverse impacts on the environment, human rights, and
              communities. This certification is increasingly important for
              businesses looking to improve their supply chain management, meet
              sustainability targets, and build trust with stakeholders and
              customers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO 42000 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO 42000 provides organizations with a systematic approach to
              implement sustainable procurement practices that support ethical
              sourcing, environmental protection, and social responsibility
              throughout the supply chain. The standard outlines the
              requirements for integrating sustainability principles into
              procurement decisions, from supplier selection to product and
              service delivery.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              Key areas of focus for ISO 42000 certification include:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Ethical sourcing: Ensuring suppliers adhere to ethical business practices.",
                "Environmental sustainability: Evaluating and reducing the environmental impact of products and services throughout their lifecycle.",
                "Social responsibility: Addressing human rights, labor practices, and community development in procurement decisions.",
                "Supply chain transparency: Ensuring traceability and accountability across the supply chain, promoting fair and responsible business conduct.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO 42000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Sustainability Criteria in Procurement",
                desc: "ISO 42000 provides clear guidelines for integrating sustainability into procurement decision-making. It requires organizations to evaluate suppliers based on their environmental impact, social practices, and ethical standards.",
              },
              {
                icon: Users,
                title: "Ethical Sourcing and Supplier Evaluation",
                desc: "The standard emphasizes the importance of choosing suppliers who demonstrate ethical behavior, respect human rights, and follow responsible environmental practices. It ensures that businesses do not engage in practices that contribute to child labor, unsafe working conditions, or environmental degradation.",
              },
              {
                icon: Target,
                title: "Risk Management in the Supply Chain",
                desc: "ISO 42000 helps organizations identify and mitigate supply chain risks related to environmental, social, and ethical factors, ensuring that procurement decisions contribute to the long-term sustainability of the organization and its suppliers.",
              },
              {
                icon: Globe,
                title: "Stakeholder Engagement",
                desc: "The standard encourages organizations to engage with stakeholders, including suppliers, customers, and communities, to ensure that their procurement practices align with broader social responsibility and sustainability goals.",
              },
              {
                icon: TrendingUp,
                title: "Continuous Improvement and Transparency",
                desc: "ISO 42000 promotes continuous improvement by encouraging organizations to monitor, measure, and report on their sustainable procurement practices, ensuring transparency and accountability in the supply chain.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO 42000 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Sustainable Sourcing Practices — ISO 42000 ensures that your procurement activities prioritize sustainability, ethical sourcing, and social responsibility, contributing to a positive impact on the environment, society, and economy.",
                "Compliance with Global Standards — Achieving ISO 42000 certification helps organizations align with global sustainability goals and corporate social responsibility (CSR) requirements. It demonstrates compliance with environmental and social standards, ensuring that your organization meets regulatory requirements and international best practices.",
                "Competitive Advantage — ISO 42000 certification provides a competitive edge by demonstrating your commitment to sustainable procurement. This can help attract customers, investors, and partners who value ethical business practices and environmental responsibility.",
                "Improved Supplier Relationships — The certification helps organizations build stronger, more collaborative relationships with suppliers who share a commitment to sustainability. It fosters trust and transparency, ensuring that your supply chain is responsible and resilient.",
                "Risk Mitigation and Cost Savings — ISO 42000 helps organizations identify and mitigate supply chain risks related to environmental impact, human rights violations, and unethical practices. By implementing sustainable procurement practices, organizations can avoid legal issues, reduce costs, and improve long-term supplier stability.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO 42000 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO 42000 includes a range of requirements and guidelines for
              integrating sustainability into procurement practices. Key
              components include:
            </p>
            <div className="space-y-4">
              {[
                "1. Sustainability Policy and Objectives — Organizations must establish a clear sustainability policy that outlines their commitment to ethical sourcing, environmental protection, and social responsibility. This policy should be integrated into overall procurement strategies and objectives.",
                "2. Supplier Evaluation and Selection — ISO 42000 requires organizations to evaluate potential suppliers based on their sustainability practices. This includes assessing their environmental impact, social compliance, and ethical standards in line with the organization’s sustainability goals.",
                "3. Risk Assessment and Mitigation — The standard requires businesses to assess supply chain risks related to environmental sustainability, labor rights, and ethical practices. Businesses must implement mitigation strategies to address these risks and ensure responsible sourcing.",
                "4. Stakeholder Communication — ISO 42000 emphasizes the importance of engaging stakeholders—including suppliers, customers, and communities—in the sustainable procurement process. This engagement ensures that the organization’s procurement activities align with broader sustainability and corporate social responsibility goals.",
                "5. Performance Monitoring and Reporting — Businesses must monitor and report on their sustainable procurement practices, ensuring transparency and accountability in the supply chain. This includes regular assessments of supplier performance and the impact of procurement decisions on sustainability goals.",
                "6. Continuous Improvement — ISO 42000 encourages continuous improvement in sustainable procurement practices by regularly reviewing and updating procurement policies, procedures, and controls to reflect changing environmental, social, and regulatory conditions.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO 42000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Enhanced Sustainability Practices",
                "Compliance with Environmental and Social Regulations",
                "Strengthened Supplier Relationships",
                "Attracting Eco-Conscious Customers",
                "Competitive Advantage and Market Leadership",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO 42000 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              Achieving ISO 42000 certification involves the following key
              steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Develop a Sustainability Policy — Create a sustainability policy that outlines your organization’s commitment to sustainable procurement, ethical sourcing, and social responsibility.",
                "2. Assess and Select Sustainable Suppliers — Evaluate your suppliers based on their sustainability practices, ensuring that they meet your organization’s ethical and environmental standards.",
                "3. Implement Risk Mitigation Strategies — Identify supply chain risks related to environmental impact, labor practices, and ethical sourcing. Develop and implement mitigation strategies to minimize these risks.",
                "4. Monitor and Measure Performance — Regularly monitor and measure the performance of suppliers and procurement activities to ensure that sustainability objectives are being met.",
                "5. Engage Stakeholders — Engage with stakeholders, including suppliers, customers, and employees, to ensure that procurement activities align with broader sustainability goals.",
                "6. Third-Party Certification Audit — Engage a recognized certification body to conduct an audit and verify compliance with ISO 42000 standards.",
                "7. Continuous Monitoring and Improvement — Continuously monitor, assess, and improve your sustainable procurement practices to ensure ongoing compliance and alignment with global sustainability objectives.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO 42000 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides expert ISO 42000 certification
              consultancy services, helping businesses establish, implement, and
              maintain sustainable procurement practices to ensure ethical
              sourcing, social responsibility, and environmental sustainability.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Sustainability Policy and Procedure Development",
                "Supplier Risk Assessment and Selection",
                "Performance Monitoring and Reporting",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Continuous Improvement and Monitoring",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO 42000 certification, ensuring
              responsible procurement, sustainable sourcing, and corporate
              social responsibility.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO42000_Details;
